import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

const LIGHTBOX_STYLESHEET_ID = 'zoom-lightbox-stylesheet';
const SWIPE_THRESHOLD = 50;

const LIGHTBOX_ICONS = {
  previous:
    '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"><path d="M15 4.5 7.5 12l7.5 7.5"/></svg>',
  next: '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"><path d="M9 4.5 16.5 12 9 19.5"/></svg>',
  close:
    '<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"><path d="M5 5l14 14M19 5 5 19"/></svg>',
};

/**
 * Loads assets/zoom-lightbox.css once, from the same folder as this script.
 */
function ensureLightboxStylesheet() {
  if (document.getElementById(LIGHTBOX_STYLESHEET_ID)) return;

  const href = new URL('zoom-lightbox.css', import.meta.url);
  href.search = new URL(import.meta.url).search;

  const link = document.createElement('link');
  link.id = LIGHTBOX_STYLESHEET_ID;
  link.rel = 'stylesheet';
  link.href = href.toString();
  document.head.append(link);
}

/**
 * The element that takes part in the open/close view transition: the media box
 * itself rather than the full-size slide, so the animation lands on the image.
 * @param {HTMLElement | undefined} element
 * @returns {HTMLElement | undefined}
 */
function getTransitionTarget(element) {
  if (!element) return element;
  const inner = element.querySelector('.product-media');
  return inner instanceof HTMLElement ? inner : element;
}

/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  #activeIndex = 0;

  /** @type {{ x: number, y: number, target: Element } | null} */
  #touchStart = null;

  connectedCallback() {
    super.connectedCallback();
    ensureLightboxStylesheet();
    this.#renderNavigation();

    const { dialog } = this.refs;
    dialog.addEventListener('scroll', this.handleScroll);
    dialog.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    dialog.addEventListener('touchend', this.#handleTouchEnd, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    const { dialog } = this.refs;
    dialog.removeEventListener('scroll', this.handleScroll);
    dialog.removeEventListener('touchstart', this.#handleTouchStart);
    dialog.removeEventListener('touchend', this.#handleTouchEnd);
  }

  /**
   * Adds the previous / close / next buttons under the image.
   */
  #renderNavigation() {
    const { dialog, media } = this.refs;
    if (!dialog || dialog.querySelector(':scope > .zoom-lightbox__nav')) return;

    const hasMultiple = Array.isArray(media) && media.length > 1;
    const closeLabel =
      dialog.querySelector('.dialog-zoomed-gallery__close-button')?.getAttribute('aria-label') || 'Close';

    const nav = document.createElement('div');
    nav.className = 'zoom-lightbox__nav';

    if (hasMultiple) {
      nav.append(this.#createNavButton('previous', 'Previous image', () => this.previous()));
    }

    const closeButton = this.#createNavButton('close', closeLabel, () => this.close());
    closeButton.autofocus = true;
    nav.append(closeButton);

    if (hasMultiple) {
      nav.append(this.#createNavButton('next', 'Next image', () => this.next()));
    }

    dialog.append(nav);
  }

  /**
   * @param {'previous' | 'next' | 'close'} name
   * @param {string} label
   * @param {() => void} onClick
   */
  #createNavButton(name, label, onClick) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `zoom-lightbox__button zoom-lightbox__button--${name}`;
    button.setAttribute('aria-label', label);
    button.innerHTML = LIGHTBOX_ICONS[name];
    button.addEventListener('click', onClick);
    return button;
  }

  /**
   * Shows the next media item, wrapping to the first.
   */
  next() {
    this.#goTo(this.#activeIndex + 1);
  }

  /**
   * Shows the previous media item, wrapping to the last.
   */
  previous() {
    this.#goTo(this.#activeIndex - 1);
  }

  /**
   * @param {number} index
   */
  #goTo(index) {
    const count = this.refs.media?.length ?? 0;
    if (count < 2) return;

    const target = ((index % count) + count) % count;
    this.selectThumbnail(target, { behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    const target = event.target;

    if (event.touches.length !== 1 || !(target instanceof Element) || !target.closest('.dialog-zoomed-gallery')) {
      this.#touchStart = null;
      return;
    }

    const touch = event.touches[0];
    if (!touch) return;

    this.#touchStart = { x: touch.clientX, y: touch.clientY, target };
  };

  /**
   * Swipe left / right to change image when the image is not zoomed in.
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    const start = this.#touchStart;
    this.#touchStart = null;

    if (!start || event.touches.length > 0) return;

    const touch = event.changedTouches[0];
    if (!touch) return;

    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;

    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy) * 1.5) return;

    const wrapper = start.target.closest('drag-zoom-wrapper');
    const scale =
      wrapper instanceof HTMLElement ? parseFloat(wrapper.style.getPropertyValue('--drag-zoom-scale')) || 1 : 1;

    if (scale > 1.05) return;

    if (dx < 0) {
      this.next();
    } else {
      this.previous();
    }
  };

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const mediaIndex = Number(index) || 0;
    this.#activeIndex = mediaIndex;

    const { dialog, media, thumbnails } = this.refs;
    const targetSlide = media[mediaIndex];
    const targetImage = getTransitionTarget(targetSlide);
    const targetThumbnail = thumbnails.children[mediaIndex];

    const open = () => {
      dialog.showModal();

      targetThumbnail?.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'center' });
      targetSlide?.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'start' });
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) {
      open();
      this.selectThumbnail(mediaIndex, { behavior: 'instant' });
      return;
    }

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(mediaIndex, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.#activeIndex = activeIndex;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    const transitionSource = getTransitionTarget(mostVisibleElement) ?? mostVisibleElement;

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    transitionSource.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      transitionSource.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Escape closes the dialog; left / right arrows change image.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
      return;
    }

    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(Number(index), { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    index = Number(index);

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.#activeIndex = index;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
        block: 'nearest',
        inline: 'start',
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
